import { format0 } from 'utils/number';

/**
 * The label after the name of a Tract a package holds only in part: how many of
 * its Subtracts the package holds, of how many it has, e.g. "(2 of 3 Subtracts)".
 * Null for a Tract held whole, which has no held count. Every table that names a
 * Tract held in part reads its label here, so the wording changes in one place;
 * this wording is the one proposed for product review.
 *
 * @param {number | null | undefined} held - The Subtracts the package holds
 * @param {number | null | undefined} total - All the Tract's Subtracts
 * @return {string | null} The label, or null for a Tract held whole
 */
export const subtractCoverageLabel = (
  held: number | null | undefined,
  total: number | null | undefined
): string | null =>
  held === null || held === undefined || total === null || total === undefined
    ? null
    : `(${format0(held)} of ${format0(total)} Subtracts)`;

/**
 * A Tract's name with its label after it, as the tables show it; the name alone
 * for a Tract held whole. For a label written as text (a chart axis, a legend, a
 * message) rather than beside a link.
 *
 * @param {string} name - The Tract's name
 * @param {string | null | undefined} label - Its `subtractCoverageLabel`, or null
 * @return {string} The name, then the label when there is one
 */
export const withSubtractCoverage = (
  name: string,
  label: string | null | undefined
): string => (label ? `${name} ${label}` : name);

/**
 * The label of a Tract row of the Eval 3.0 tables, which say whether the package holds the
 * Tract in part (`heldInPart`) beside its Subtracts Held and Subtracts in Tract: those two as
 * `subtractCoverageLabel` words them, or null for a Tract held whole and any other row.
 *
 * @param {object} row - The row
 * @param {boolean} [row.heldInPart] - Whether the package holds the Tract in part
 * @param {number | null} [row.subtractsHeld] - The Subtracts the package holds
 * @param {number | null} [row.subtractsInTract] - All the Tract's Subtracts
 * @return {string | null} The label, or null
 */
export const tractRowCoverageLabel = (row: {
  heldInPart?: boolean | null;
  subtractsHeld?: number | null;
  subtractsInTract?: number | null;
}): string | null =>
  row.heldInPart
    ? subtractCoverageLabel(row.subtractsHeld, row.subtractsInTract)
    : null;

/**
 * A label as it follows a Tract's name or link in a table cell: a space, then the label; null
 * when there is none, which React renders as nothing.
 *
 * @param {string | null | undefined} label - A `subtractCoverageLabel`, or null
 * @return {string | null} The label with its leading space, or null
 */
export const subtractCoverageSuffix = (
  label: string | null | undefined
): string | null => (label ? ` ${label}` : null);
